import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';

import { RequireAuth, RequireOwner } from '@features/auth';
import { OrderStageGuard } from '@features/searches';
import { AboutPage } from '@pages/about/AboutPage';
import { AnalysisPage } from '@pages/analysis/AnalysisPage';
import { AppHomePage } from '@pages/app-home/AppHomePage';
import { AppVehiclePage } from '@pages/app-vehicle/AppVehiclePage';
import { ChannelsPage } from '@pages/channels/ChannelsPage';
import { ConclusionPage } from '@pages/conclusion/ConclusionPage';
import { FaqPage } from '@pages/faq/FaqPage';
import { HandoverPage } from '@pages/handover/HandoverPage';
import { MandatePage } from '@pages/mandate/MandatePage';
import { NegotiationPage } from '@pages/negotiation/NegotiationPage';
import { NegotiationsPage } from '@pages/negotiations/NegotiationsPage';
import { OutreachPage } from '@pages/outreach/OutreachPage';
import { PaymentPage } from '@pages/payment/PaymentPage';
import { Top4Page } from '@pages/top4/Top4Page';
import { AssistantPage } from '@pages/assistant/AssistantPage';
import { HomePage } from '@pages/home/HomePage';
import {
  AccessibilityPage,
  CookiesPage,
  ImprintPage,
  PrivacyPage,
  TermsPage,
  WithdrawalFormPage,
  WithdrawalPage,
} from '@pages/legal';
import { LoginPage } from '@pages/login/LoginPage';
import { NotFoundPage } from '@pages/not-found/NotFoundPage';
import { ResultsPage } from '@pages/results/ResultsPage';
import { ROUTES } from '@shared/config/routes';

import { AppLayout } from './layouts/AppLayout';
import { PublicLayout } from './layouts/PublicLayout';
import { RootLayout } from './layouts/RootLayout';
import { RouteErrorBoundary } from './RouteErrorBoundary';

export const routes: RouteObject[] = [
  {
    // Общий корень: над ним живёт окно входа, доступное с любого экрана.
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <PublicLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { path: ROUTES.home, element: <HomePage /> },
          { path: ROUTES.about, element: <AboutPage /> },
          { path: ROUTES.faq, element: <FaqPage /> },
          { path: ROUTES.login, element: <LoginPage /> },
          // `/verify` — часть того же потока: шаг с кодом живёт внутри страницы
          // входа, потому что без активного запроса показывать на нём нечего.
          { path: ROUTES.verify, element: <LoginPage /> },
          // Правовые страницы. Доступны без входа и без оплаты — этого требует
          // § 5 DDG («ständig verfügbar») и Art. 246a §4 EGBGB: сведения должны
          // быть доступны **до** заключения договора, а не после.
          { path: ROUTES.imprint, element: <ImprintPage /> },
          { path: ROUTES.privacy, element: <PrivacyPage /> },
          { path: ROUTES.terms, element: <TermsPage /> },
          { path: ROUTES.withdrawal, element: <WithdrawalPage /> },
          // Widerrufsfunktion (§ 356a BGB): вторая ступень — на этом же адресе,
          // вход не требуется.
          { path: ROUTES.withdrawalForm, element: <WithdrawalFormPage /> },
          { path: ROUTES.cookies, element: <CookiesPage /> },
          { path: ROUTES.accessibility, element: <AccessibilityPage /> },
          // Прежние английские адреса уже разосланы в письмах: ломать их
          // нельзя, поэтому они остаются редиректами.
          {
            path: ROUTES.legacyPrivacy,
            element: <Navigate to={ROUTES.privacy} replace />,
          },
          { path: ROUTES.legacyTerms, element: <Navigate to={ROUTES.terms} replace /> },
          { path: ROUTES.legacyImprint, element: <Navigate to={ROUTES.imprint} replace /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
      {
        element: <AppLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          // `/app` — список запросов и стартовая страница личной части
          // (MVP_PRD §20). Всё остальное живёт под идентификатором запроса.
          {
            // `/app` — список заказов у зарегистрированного и **единственный
            // заказ** у гостя (MVP_PRD §41.2). Шаги 1–2 доступны без
            // регистрации, поэтому guard пропускает и гостя; настоящая проверка
            // прав — на backend, здесь только экран.
            path: ROUTES.app,
            element: (
              <RequireOwner pendingType="assistant.start">
                <AppHomePage />
              </RequireOwner>
            ),
          },
          // `OrderStageGuard` — навигационное удобство, а не защита (FE-200):
          // он уводит с экрана, до которого заказ ещё не дошёл. Права
          // проверяет backend, и guard его не заменяет.
          {
            path: ROUTES.searchAssistant(),
            element: (
              <RequireOwner pendingType="assistant.start">
                <OrderStageGuard screen="assistant">
                  <AssistantPage />
                </OrderStageGuard>
              </RequireOwner>
            ),
          },
          {
            // Экран анализа — шаг 2: прогон по каталогу и его настоящие числа.
            // Доступен гостю: шаги 1–2 проходятся без регистрации (§41.2).
            path: ROUTES.searchAnalysis(),
            element: (
              <RequireOwner>
                <OrderStageGuard screen="analysis">
                  <AnalysisPage />
                </OrderStageGuard>
              </RequireOwner>
            ),
          },
          {
            path: ROUTES.searchResults(),
            element: (
              // Закрытая витрина рабочего набора — шаг 2, он же доступен гостю.
              <RequireOwner>
                <OrderStageGuard screen="results">
                  <ResultsPage />
                </OrderStageGuard>
              </RequireOwner>
            ),
          },
          {
            // Шаг 4: четвёрка. Требует аккаунта и оплаты — настоящую проверку
            // делает backend (`402 PAYMENT_REQUIRED`), guard лишь не ведёт на
            // экран, до которого заказ не дошёл.
            path: ROUTES.searchTop4(),
            element: (
              <RequireAuth>
                <OrderStageGuard screen="top4">
                  <Top4Page />
                </OrderStageGuard>
              </RequireAuth>
            ),
          },
          {
            // Оплата — единственный экран воронки, который требует аккаунта:
            // шаги 1–2 гость проходит целиком (MVP_PRD §41.2).
            path: ROUTES.searchPayment(),
            element: (
              <RequireAuth>
                <PaymentPage />
              </RequireAuth>
            ),
          },
          {
            // Шаг 4: поручение на запрос сведений (FE-170). Требует аккаунта и
            // оплаты — настоящую проверку делает backend (`402`).
            path: ROUTES.searchOutreach(),
            element: (
              <RequireAuth>
                <OutreachPage />
              </RequireAuth>
            ),
          },
          {
            // Шаг 6: заключение по сделке и финальная сверка (FE-250). Требует
            // аккаунта и оплаты — настоящую проверку делает backend (`402`).
            path: ROUTES.searchConclusion(),
            element: (
              <RequireAuth>
                <ConclusionPage />
              </RequireAuth>
            ),
          },
          {
            // Шаг 7: сопровождение покупки (FE-260). Требует аккаунта и оплаты
            // — настоящую проверку делает backend (`402`), а `404
            // HANDOVER_NOT_FOUND` отвечает заказу, который до шага не дошёл.
            path: ROUTES.searchHandover(),
            element: (
              <RequireAuth>
                <OrderStageGuard screen="handover">
                  <HandoverPage />
                </OrderStageGuard>
              </RequireAuth>
            ),
          },
          {
            // Шаг 5: мандат на торг по взятому в торг автомобилю (FE-171).
            path: ROUTES.searchMandate(),
            element: (
              <RequireAuth>
                <MandatePage />
              </RequireAuth>
            ),
          },
          {
            // Каналы уведомлений (FE-160). Требует аккаунта: у гостя
            // переговоров не бывает, а канал принадлежит пользователю.
            //
            // Объявлен **раньше** `/app/negotiations/:negotiationId`: иначе
            // `channels` разбирался бы как идентификатор переписки.
            path: ROUTES.negotiationChannels,
            element: (
              <RequireAuth>
                <ChannelsPage />
              </RequireAuth>
            ),
          },
          {
            // Список переписок (FE-172). Вне воронки заказа: переписка
            // переживает его удаление (§23.10).
            path: ROUTES.negotiations,
            element: (
              <RequireAuth>
                <NegotiationsPage />
              </RequireAuth>
            ),
          },
          {
            // Карточка переписки и решение по финальному предложению
            // (FE-173, FE-174).
            path: ROUTES.negotiation(),
            element: (
              <RequireAuth>
                <NegotiationPage />
              </RequireAuth>
            ),
          },
          {
            // Экран автомобиля своего рабочего набора открыт и гостю (Epic 58,
            // FE-587): данные автомобилей набора бесплатны, а регистрация
            // вызывается только при попытке оплатить (MVP_PRD §41.1). Уровень
            // доступа (`open` / `full` / `teaser`) решает backend по сессии
            // или гостевому cookie.
            path: ROUTES.searchVehicle(),
            element: (
              <RequireOwner>
                <AppVehiclePage />
              </RequireOwner>
            ),
          },
          // Старые адреса без идентификатора запроса ведут в список: какой
          // именно запрос имелся в виду, из них не следует, а открывать
          // «какой-нибудь» — это показать чужие результаты (AC-07).
          { path: ROUTES.legacyAssistant, element: <Navigate to={ROUTES.app} replace /> },
          { path: ROUTES.legacyResults, element: <Navigate to={ROUTES.app} replace /> },
          { path: ROUTES.legacyAppVehicle, element: <Navigate to={ROUTES.app} replace /> },
          // Экранов избранного, наборов сравнения, профиля и админки в MVP нет
          // (CLAUDE.md §2): маршрутов под них не держим, адрес отвечает 404
          // публичной оболочки, как любой неизвестный.
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
